// Tier-aware gating helpers.
//
// Backend already enforces tier via 402/403 envelopes, but those are
// terminal errors that surface as red toasts. This block surfaces
// upgrade-required paths *before* the user clicks a paid action — so
// the journey ends with a graceful upgrade CTA, not a backend error.
//
// Two consumers:
//
//   1. <RequirePaidTier> — wraps a whole page (e.g. /billing/pillars).
//      Free users see the upgrade CTA instead of the underlying page.
//
//   2. <UpgradeInlineBanner /> — drop-in inline banner that pages can
//      render above a paid CTA ("Buy now") so the user understands
//      they'll be redirected on click.
//
// Tier semantics, mirrored from foundation/users User model:
//   - free            : registered, no paid plan
//   - paid_individual : single-seat paid plan
//   - paid_org        : team / enterprise plan
//
// Anything other than 'free' counts as "paid" by default. The
// `minTier` prop can pin the gate to a specific tier (e.g. `paid_org`
// for seat-management pages).

import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { meetsRequirement } from '../../config/plans';
import { upgradeHref, hereAsReturnTo } from '../../modules/billing/returnTo.js';

import { Text } from '../elements/text.element.jsx';
const BRAND = 'var(--moksy-brand)';

// The gate knows the page it is standing in front of, so the upgrade link
// carries it: once the plan is confirmed, billing hands the member straight
// back here instead of leaving them on the Activity tab (ACT-MONEY-001/-059).
const useUpgradeHref = (minTier) => {
  const location = useLocation();
  return upgradeHref({ returnTo: hereAsReturnTo(location), plan: minTier === 'paid_any' ? 'pro' : minTier });
};

export const UpgradeCard = ({ title, body, minTier = 'paid_any' }) => {
  const href = useUpgradeHref(minTier);
  const tierCopy = minTier === 'paid_org' ? 'a team plan' : 'a paid plan';
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-orange-200 bg-orange-50 p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-2xl">⭐</span>
        <div className="flex-1">
          <Text as="h2" variant="title">{title}</Text>
          <p className="mt-2 text-sm text-orange-900/90 leading-relaxed">
            {body || `This page needs ${tierCopy}. Upgrade to unlock it — your project stays exactly where it is.`}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <Link
              to={href}
              className="rounded-full px-4 py-2 text-xs font-semibold text-white"
              style={{ backgroundColor: BRAND }}
            >
              View plans
            </Link>
            <Link to="/projects" className="text-xs font-semibold text-orange-900 underline-offset-2 hover:underline">
              Back to projects
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RequirePaidTier = ({ minTier = 'paid_any', title, body, children }) => {
  const { user } = useAuthContext();
  // Owner-group operators are not customers — they bypass tier gates
  // entirely. The page itself decides what content makes sense for
  // them; we just don't block.
  if (String(user?.group || '').toLowerCase() === 'owner') return children;
  if (meetsRequirement(user, minTier)) return children;
  return (
    <div className="px-6 py-16">
      <UpgradeCard title={title || 'Upgrade to continue'} body={body} minTier={minTier} />
    </div>
  );
};

export const UpgradeInlineBanner = ({ minTier = 'paid_any', body }) => {
  const { user } = useAuthContext();
  const href = useUpgradeHref(minTier);
  if (String(user?.group || '').toLowerCase() === 'owner') return null;
  if (meetsRequirement(user, minTier)) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
      <div className="flex items-center gap-2">
        <span aria-hidden="true">⭐</span>
        <span>{body || 'This is a paid-plan feature.'}</span>
      </div>
      <Link
        to={href}
        className="rounded-full px-3 py-1 text-[11px] font-semibold text-white"
        style={{ backgroundColor: BRAND }}
      >
        Upgrade
      </Link>
    </div>
  );
};

export default RequirePaidTier;
